import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TemplateId } from "@app/shared/ir";
import { TEMPLATE_INFO } from "@app/shared/templates";
import { Player } from "../animation/Player";
import { tagSvgPaths, analyzeSvg } from "@app/shared/analyze";
import { compileTimeline } from "@app/shared/templates";
import { AnimationIR } from "@app/shared/ir";
import { TEMPLATE_BRAND } from "../animation/ai-icons";

export function Templates() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <div className="mb-10 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white">All templates</h1>
          <p className="mt-1 text-sm text-white/55">
            Twelve hand-tuned motion presets. Each is a deterministic function from your IR → an SVG timeline, then run through GSAP for preview. The marks below are real AI brand logos to showcase how each template handles different geometries.
          </p>
        </div>
        <Link to="/signup" className="btn-primary">Try it free <ArrowRight className="h-4 w-4" /></Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TemplateId.options.map((id, i) => (
          <Card key={id} id={id} index={i} />
        ))}
      </div>
    </div>
  );
}

function Card({ id, index }: { id: string; index: number }) {
  const info = TEMPLATE_INFO[id] ?? { title: id, blurb: "", bestFor: [] };
  const brand = TEMPLATE_BRAND[id];
  const svg = tagSvgPaths(brand.svg);
  const analysis = analyzeSvg(svg);
  const ir = AnimationIR.parse({
    templateId: id as any,
    durationMs: 1600,
    easing: "easeOutExpo",
    mood: "minimal",
    effects: [],
    colorOverrides: { transparent: true },
  });
  const timeline = compileTimeline(ir, analysis);
  return (
    <div className="card card-hover overflow-hidden">
      <div className="aspect-video w-full grid-bg relative">
        <span className="absolute left-3 top-3 z-10 rounded-md bg-black/30 px-2 py-0.5 font-mono text-[10px] text-white/60 backdrop-blur-sm">
          {brand.title}
        </span>
        <div className="grid h-full place-items-center">
          {/* delayMs staggers each card's clock so the page never catches all
              cards at "opacity 0" at the same time. */}
          <Player svg={svg} timeline={timeline} loop autoplay delayMs={index * 220} />
        </div>
      </div>
      <div className="border-t border-white/[0.06] p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-white">{info.title}</span>
          <span className="font-mono text-[10px] text-white/30">{id}</span>
        </div>
        <p className="mt-1 text-xs text-white/50">{info.blurb}</p>
        {info.bestFor.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {info.bestFor.map((b) => (
              <span key={b} className="rounded-md bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-white/50">{b}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
